/**
 * JSONLogic evaluator — TypeScript port of backend/app/domains/workflow/jsonlogic.py.
 *
 * The frontend uses this to evaluate `visible_when`, `required_when`, and `enabled_when`
 * on form fields, and to preview transition guards locally before submitting.
 *
 * Both sides MUST stay in sync — if you add an operator here, mirror it in the Python
 * module (and vice-versa). The reference spec is https://jsonlogic.com/.
 */

export type JsonLogic =
  | string
  | number
  | boolean
  | null
  | JsonLogic[]
  | { [op: string]: JsonLogic };

import { toText } from "./toText";

export type Data = Record<string, unknown>;

export class JsonLogicError extends Error {}

// ─── Variable resolution ───────────────────────────────────────────
function getVar(data: Data, path: unknown, defaultVal: unknown = null): unknown {
  if (path === null || path === undefined || path === "") return data;
  let p = path;
  let dflt = defaultVal;
  if (Array.isArray(p)) {
    dflt = p.length > 1 ? p[1] : null;
    p = p[0];
  }
  if (typeof p !== "string") return dflt;

  let cur: unknown = data;
  for (const part of p.split(".")) {
    if (cur && typeof cur === "object" && !Array.isArray(cur) && part in cur) {
      cur = (cur as Record<string, unknown>)[part];
    } else if (Array.isArray(cur)) {
      const idx = Number.parseInt(part, 10);
      if (Number.isNaN(idx) || idx < 0 || idx >= cur.length) return dflt;
      cur = cur[idx];
    } else {
      return dflt;
    }
  }
  return cur;
}

// ─── Truthiness (JSONLogic semantics — JS-like) ────────────────────
function truthy(v: unknown): boolean {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === "number" && v === 0) return false;
  if (typeof v === "string" && v === "") return false;
  if (Array.isArray(v) && v.length === 0) return false;
  if (typeof v === "object" && v !== null && Object.keys(v).length === 0) return false;
  return true;
}

function looseEq(a: unknown, b: unknown): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (typeof a === typeof b) return a === b;
  // Cross-type loose comparison
  const na = Number(a as number);
  const nb = Number(b as number);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return toText(a) === toText(b);
}

// ─── Eager operators ───────────────────────────────────────────────
type EagerOp = (args: unknown[], data: Data) => unknown;

const OPS: Record<string, EagerOp> = {
  "==": (a) => looseEq(a[0], a[1]),
  "===": (a) => typeof a[0] === typeof a[1] && a[0] === a[1],
  "!=": (a) => !looseEq(a[0], a[1]),
  "!==": (a) => !(typeof a[0] === typeof a[1] && a[0] === a[1]),
  "<":  (a) => a.slice(0, -1).every((_, i) => (a[i] as number) <  (a[i + 1] as number)),
  "<=": (a) => a.slice(0, -1).every((_, i) => (a[i] as number) <= (a[i + 1] as number)),
  ">":  (a) => a.slice(0, -1).every((_, i) => (a[i] as number) >  (a[i + 1] as number)),
  ">=": (a) => a.slice(0, -1).every((_, i) => (a[i] as number) >= (a[i + 1] as number)),
  "!":  (a) => !truthy(a[0]),
  "!!": (a) => truthy(a[0]),
  "+":  (a) => a.reduce<number>((acc, v) => acc + Number(v), 0),
  "-":  (a) =>
    a.length === 1
      ? -Number(a[0])
      : Number(a[0]) - a.slice(1).reduce<number>((acc, v) => acc + Number(v), 0),
  "*":  (a) => a.reduce<number>((acc, v) => acc * Number(v), 1),
  "/":  (a) => Number(a[0]) / Number(a[1]),
  "%":  (a) => Number(a[0]) % Number(a[1]),
  in: (a) => {
    const [needle, haystack] = a;
    if (haystack === null || haystack === undefined) return false;
    if (typeof haystack === "string") return haystack.includes(String(needle));
    if (Array.isArray(haystack)) return haystack.some((x) => looseEq(x, needle));
    return false;
  },
  starts_with: (a) => typeof a[0] === "string" && a[0].startsWith(String(a[1])),
  ends_with:   (a) => typeof a[0] === "string" && a[0].endsWith(String(a[1])),
  missing: (a, data) => (a as string[]).filter((k) => getVar(data, k) === null || getVar(data, k) === undefined),
  missing_some: (a, data) => {
    const [min, keys] = a as [number, string[]];
    const missing = keys.filter((k) => getVar(data, k) === null || getVar(data, k) === undefined);
    return keys.length - missing.length >= min ? [] : missing;
  },
};

// ─── Lazy operators ────────────────────────────────────────────────
function lazyAnd(arr: JsonLogic[], data: Data): unknown {
  let last: unknown = true;
  for (const sub of arr) {
    last = evaluate(sub, data);
    if (!truthy(last)) return last;
  }
  return last;
}

function lazyOr(arr: JsonLogic[], data: Data): unknown {
  let last: unknown = false;
  for (const sub of arr) {
    last = evaluate(sub, data);
    if (truthy(last)) return last;
  }
  return last;
}

function lazyIf(arr: JsonLogic[], data: Data): unknown {
  let i = 0;
  while (i < arr.length - 1) {
    if (truthy(evaluate(arr[i], data))) return evaluate(arr[i + 1], data);
    i += 2;
  }
  return i < arr.length ? evaluate(arr[i], data) : null;
}

const LAZY: Record<string, (arr: JsonLogic[], data: Data) => unknown> = {
  and: lazyAnd,
  or: lazyOr,
  if: lazyIf,
  "?:": lazyIf,
};

// ─── Public API ────────────────────────────────────────────────────
export function evaluate(logic: JsonLogic, data: Data = {}): unknown {
  if (logic === null || typeof logic !== "object") return logic;
  if (Array.isArray(logic)) return logic.map((x) => evaluate(x, data));

  const keys = Object.keys(logic);
  if (keys.length !== 1) {
    throw new JsonLogicError(`Expression must have exactly one operator, got ${JSON.stringify(logic)}`);
  }
  const op = keys[0];
  const rawArgs = (logic as Record<string, JsonLogic>)[op];

  if (op in LAZY) {
    const arr = Array.isArray(rawArgs) ? rawArgs : [rawArgs];
    return LAZY[op](arr, data);
  }

  if (op === "var") {
    const arr = Array.isArray(rawArgs) ? rawArgs : [rawArgs];
    return getVar(data, arr[0], arr.length > 1 ? arr[1] : null);
  }

  if (!(op in OPS)) {
    throw new JsonLogicError(`Unknown operator: ${op}`);
  }

  const args = Array.isArray(rawArgs)
    ? rawArgs.map((a) => evaluate(a, data))
    : [evaluate(rawArgs, data)];

  return OPS[op](args, data);
}

/**
 * Evaluate a guard / visible / required / enabled rule as a boolean.
 * An empty / null / undefined rule is treated as a permissive no-op (returns true).
 */
export function isSatisfied(logic: JsonLogic | null | undefined, data: Data = {}): boolean {
  if (logic === null || logic === undefined) return true;
  if (typeof logic === "object" && !Array.isArray(logic) && Object.keys(logic).length === 0) return true;
  if (Array.isArray(logic) && logic.length === 0) return true;
  return truthy(evaluate(logic, data));
}
